import { useState } from 'react';
import type { ApiResponse } from 'vopay';
import { api } from '@/services/api.config';

type ScheduleType = 'fund' | 'withdraw';

/**
 * Edits and cancels a single upcoming payment of a legacy (VIOEftScheduledPayments)
 * schedule. Keyed by ScheduledTransactionID + the payment's own date, unlike the
 * new-table /scheduled-payment/upcoming-payment/* endpoints, which key off a detail
 * row ID. The legacy table has no dedicated cancel-one-payment endpoint, so cancel
 * is an edit that sets the amount to 0.
 */
export default function useLegacyScheduledPayment() {
  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  async function edit(
    scheduleType: ScheduleType,
    scheduledTransactionId: string,
    upcomingPayment: string,
    values: { amount?: string; date?: string },
  ): Promise<ApiResponse> {
    const editApi = scheduleType === 'fund' ? api.postEditScheduledFund : api.postEditScheduledWithdraw;

    const res = await editApi({
      ScheduledTransactionID: scheduledTransactionId,
      UpcomingPayment: upcomingPayment,
      ...(values.amount !== undefined ? { Amount: values.amount } : {}),
      ...(values.date ? { NewDate: values.date } : {}),
    });

    if (!res.Success) {
      throw new Error(res.ErrorMessage);
    }

    return res;
  }

  async function editLegacyScheduledPayment(
    scheduleType: ScheduleType,
    scheduledTransactionId: string,
    upcomingPayment: string,
    values: { amount?: string; date?: string },
  ): Promise<ApiResponse> {
    try {
      setEditing(true);
      return await edit(scheduleType, scheduledTransactionId, upcomingPayment, values);
    } catch (error) {
      console.error('Edit legacy scheduled payment failed:', error);
      throw error;
    } finally {
      setEditing(false);
    }
  }

  async function cancelLegacyScheduledPayment(
    scheduleType: ScheduleType,
    scheduledTransactionId: string,
    upcomingPayment: string,
  ): Promise<ApiResponse> {
    try {
      setCancelling(true);
      return await edit(scheduleType, scheduledTransactionId, upcomingPayment, { amount: '0' });
    } catch (error) {
      console.error('Cancel legacy scheduled payment failed:', error);
      throw error;
    } finally {
      setCancelling(false);
    }
  }

  return { editing, cancelling, editLegacyScheduledPayment, cancelLegacyScheduledPayment };
}
